import React, { useState, useEffect } from 'react';
import {
  Star, Zap, Cpu, Briefcase,
  LayoutGrid, ArrowRight, PanelLeft, X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../component/Header';
import Footer from '../component/Footer';
import { getLibrary, LibraryRow } from '../services/libraryService';
import { getDashboard } from '../services/userService';

type CourseCard = {
  id: string;
  title: string;
  category: string;
  duration?: string;
  level?: string;
  image: string;
  price?: string;
  description: string;
  link?: string;
  plan_ids?: number[];
};

type CategoryItem = {
  value: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
};

export function canOpenContent(planIds?: number[], userPlanId?: number | null): boolean {
  if (!planIds || planIds.length === 0) {
    return true;
  }

  return userPlanId !== null && userPlanId !== undefined && planIds.includes(userPlanId);
}

const courseRows: CourseCard[] = [
  {
    id: 'static-1',
    title: 'Generative AI for Leaders',
    category: 'Operations',
    duration: '8.5h',
    level: 'Intermediate',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=800&q=80',
    price: '$47',
    description: 'The world of leadership is changing-fast. Generative AI is reshaping how leaders communicate and scale their impact.',
    link: 'https://www.youtube.com/',
    plan_ids: [],
  },
  {
    id: 'static-2',
    title: 'AI Fundamentals for Professionals',
    category: 'Leadership',
    duration: '4.5h',
    level: 'Advanced',
    image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=800&q=80',
    price: '$97',
    description: 'Explore fundamental concepts and strategic implications of AI to harness potential for innovation and efficiency.',
    link: 'https://www.youtube.com/',
    plan_ids: [],
  },
  {
    id: 'static-3',
    title: 'Use AI and ChatBots to Automate',
    category: 'Operations',
    duration: '5.5h',
    level: 'Intermediate',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80',
    price: '$67',
    description: 'Unlock potential in your business. Leverage AI Automation and Content Creation tools to boost productivity.',
    link: 'https://www.youtube.com/',
    plan_ids: [],
  },
  {
    id: 'static-4',
    title: 'AI for Operations Management',
    category: 'Leadership',
    duration: '3.5h',
    level: 'All Levels',
    image: 'https://images.unsplash.com/photo-1507146426996-ef05306b995a?auto=format&fit=crop&w=800&q=80',
    price: '$47',
    description: 'Comprehensive overview of key operations principles and how AI provides significant advantages across the value chain.',
    link: 'https://www.youtube.com/',
    plan_ids: [],
  },
];

const baseCategories: CategoryItem[] = [
  { value: 'All', title: 'All Courses', icon: LayoutGrid },
  { value: 'Operations', title: 'Generative AI for Leaders', icon: Zap },
  { value: 'Branding', title: 'Use AI and ChatBots to Automate', icon: Star },
  { value: 'Leadership', title: 'AI Fundamentals for Professionals', icon: Briefcase },
  { value: 'Fundamentals', title: 'AI for Operations Management', icon: Cpu },
];

const mapLibraryRowsToCourses = (rows: LibraryRow[]): CourseCard[] =>
  rows.flatMap((row, rowIndex) =>
    row.cards.map((card, cardIndex) => ({
      id: `dynamic-${rowIndex}-${cardIndex}-${card.title}`,
      title: card.title,
      category: row.title,
      image: card.image,
      description: card.title,
      link: card.link,
      plan_ids: card.plan_ids,
    })),
  );

const Library = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('All');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [courses, setCourses] = useState<CourseCard[]>(courseRows);
  const [userPlanId, setUserPlanId] = useState<number | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const categories = courses.reduce<CategoryItem[]>((items, course) => {
    const exists = items.some((item) => item.value === course.category);
    return exists
      ? items
      : [...items, { value: course.category, title: course.category, icon: LayoutGrid }];
  }, baseCategories);

  const filteredCourses = activeCategory === 'All'
    ? courses
    : courses.filter(course => course.category === activeCategory);

  const activeCategoryObj = categories.find(cat => cat.value === activeCategory);
  const activeTitle = activeCategoryObj ? activeCategoryObj.title : 'All Courses';

  useEffect(() => {
    let isMounted = true;

    const loadLibrary = async () => {
      try {
        const rows = await getLibrary();
        const dynamicCourses = mapLibraryRowsToCourses(rows || []);

        if (isMounted && dynamicCourses.length > 0) {
          setCourses(dynamicCourses);
        }
      } catch {
        if (isMounted) {
          setCourses(courseRows);
        }
      }
    };

    loadLibrary();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const token = localStorage.getItem('token');

    if (!token) {
      setUserPlanId(null);
      return () => {
        isMounted = false;
      };
    }

    const loadDashboard = async () => {
      try {
        const response = await getDashboard(token);
        if (isMounted) {
          setUserPlanId(response.data.plan_id ?? null);
        }
      } catch {
        if (isMounted) {
          setUserPlanId(null);
        }
      }
    };

    loadDashboard();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (isSidebarOpen || showUpgradeModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; }
  }, [isSidebarOpen, showUpgradeModal]);

  const handleOpenContent = (course: CourseCard) => {
    if (!canOpenContent(course.plan_ids, userPlanId)) {
      setShowUpgradeModal(true);
      return;
    }

    if (course.link) {
      window.open(course.link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <Header />
      <div className="bg-[#fcfaf8] min-h-screen font-sans text-zinc-900">
        <div className="pt-24 pb-12 px-6 border-b border-zinc-200 bg-white">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-black text-zinc-900 tracking-tight">
              Welcome back, <span className="text-amber-500">User</span>
            </h1>
            <p className="text-zinc-500 mt-2 font-medium">Continue your path to AI mastery.</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-12 relative">
          <div className="lg:hidden mb-8">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="w-full flex items-center justify-between bg-white border border-zinc-200 p-4 rounded-2xl shadow-sm hover:border-amber-400 transition-colors"
            >
              <div className="flex items-center gap-3 shrink-0">
                <PanelLeft className="w-5 h-5 text-zinc-500" />
                <span className="font-bold text-zinc-900">Sidebar</span>
              </div>
              <div className="bg-zinc-100 px-3 py-1 rounded-lg text-sm font-bold truncate max-w-[140px] sm:max-w-[250px]">
                {activeTitle}
              </div>
            </button>
          </div>

          <div className="flex flex-col lg:flex-row gap-12">
            {isSidebarOpen && (
              <div
                className="fixed inset-0 bg-zinc-950/60 backdrop-blur-sm z-40 lg:hidden transition-opacity"
                onClick={() => setIsSidebarOpen(false)}
              />
            )}

            <aside className={`fixed inset-y-0 left-0 z-50 w-[300px] h-full overflow-y-auto bg-[#fcfaf8] transform transition-transform duration-300 ease-in-out lg:bg-transparent lg:relative lg:translate-x-0 lg:z-auto lg:h-auto lg:overflow-visible lg:w-[280px] lg:shrink-0 ${
              isSidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'
            }`}>
              <div className="p-6 lg:p-0 lg:sticky lg:top-8 space-y-6">
                <div className="flex items-center justify-between lg:hidden mb-2">
                  <h2 className="font-black text-xl text-zinc-900 tracking-tight">Sidebar</h2>
                  <button
                    onClick={() => setIsSidebarOpen(false)}
                    className="p-2 bg-white border border-zinc-200 rounded-full hover:bg-zinc-100 transition-colors"
                  >
                    <X className="w-5 h-5 text-zinc-500" />
                  </button>
                </div>

                <div className="bg-white border border-zinc-200 rounded-[2rem] p-6 shadow-xl shadow-zinc-200/40">
                  <h3 className="hidden lg:block text-xs font-black uppercase tracking-widest text-zinc-400 mb-6 px-2">
                    Categories
                  </h3>

                  <nav className="flex flex-col gap-2">
                    {categories.map((cat) => {
                      const Icon = cat.icon;
                      const isActive = activeCategory === cat.value;

                      return (
                        <button
                          key={cat.value}
                          onClick={() => {
                            setActiveCategory(cat.value);
                            setIsSidebarOpen(false);
                          }}
                          className={`group flex items-center justify-between px-4 py-3.5 rounded-xl font-bold text-sm transition-all duration-300 ${
                            isActive
                              ? 'bg-[#09090B] text-white shadow-md shadow-[#09090B]-500/20 translate-x-1'
                              : 'text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900 hover:translate-x-1 hover:border-zinc-200 border border-transparent'
                          }`}
                        >
                          <div className="flex items-center gap-3 relative z-10 text-left">
                            <Icon className={`w-4 h-4 transition-colors duration-300 ${isActive ? 'text-white' : 'text-zinc-400 group-hover:text-[#09090B]'}`} />
                            {cat.title}
                          </div>

                          <ArrowRight className={`w-4 h-4 transition-all duration-300 ${
                            isActive
                              ? 'opacity-100 translate-x-0 text-white'
                              : 'opacity-0 -translate-x-4 text-[#09090B] group-hover:opacity-100 group-hover:translate-x-0'
                          }`} />
                        </button>
                      );
                    })}
                  </nav>
                </div>
              </div>
            </aside>

            <main className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-xl font-bold text-zinc-900">
                  Showing {activeTitle} <span className="text-zinc-400 font-medium ml-2 text-sm">({filteredCourses.length} courses)</span>
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredCourses.map((course) => (
                  <button
                    type="button"
                    key={course.id}
                    onClick={() => handleOpenContent(course)}
                    className="text-left bg-white border border-zinc-200 rounded-[2rem] overflow-hidden hover:border-amber-400 hover:shadow-2xl hover:shadow-amber-500/10 transition-all duration-500 group flex flex-col h-full"
                  >
                    <div className="h-52 md:h-44 lg:h-52 relative overflow-hidden shrink-0">
                      <img
                        src={course.image}
                        alt={course.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>

                    <div className="p-8 flex flex-col flex-1">
                      <p className="text-l font-black text-zinc-900 mb-3 leading-tight transition-colors">
                        {course.description}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </main>
          </div>
        </div>
      </div>

      {showUpgradeModal && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-zinc-950/60 px-4" role="dialog" aria-modal="true" aria-labelledby="upgrade-required-title">
          <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-2xl">
            <h2 id="upgrade-required-title" className="text-2xl font-black text-zinc-950">
              Upgrade Required
            </h2>
            <p className="mt-4 text-sm font-medium leading-6 text-zinc-600">
              This content is not in your current plan. Please upgrade your plan to access it.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                type="button"
                onClick={() => setShowUpgradeModal(false)}
                className="rounded-xl border border-zinc-200 px-5 py-3 text-sm font-black uppercase tracking-widest text-zinc-700 transition-colors hover:bg-zinc-50"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={() => navigate('/pricing')}
                className="rounded-xl bg-[#09090B] px-5 py-3 text-sm font-black uppercase tracking-widest text-white transition-colors hover:bg-zinc-800"
              >
                Upgrade Plan
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default Library;
